import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { CreateSharepointConfigurationRequestDto } from '../../../infrastructure/dtos/create-sharepoint-configuration-request.dto';
import { SharepointConfigurationsRepository } from '../../../infrastructure/repositories/sharepoint-configurations.repository';
import { SharepointConfigurationsState } from '../../states/sharepoint-configurations.state';
import { CreateSharepointConfigurationResult } from './create-sharepoint-configuration.result';

/**
 * Create SharePoint Configuration use case.
 * Creates a new SharePoint configuration for the current tenant and updates state.
 */
@Injectable({ providedIn: 'root' })
export class CreateSharepointConfigurationUseCase {
  private readonly _repository = inject(SharepointConfigurationsRepository);
  private readonly _state = inject(SharepointConfigurationsState);

  /**
   * Executes the create SharePoint configuration use case.
   * @param data - The SharePoint configuration data to create
   * @returns Observable with result containing the new configuration ID
   */
  public execute(data: CreateSharepointConfigurationRequestDto): Observable<CreateSharepointConfigurationResult> {
    this._state.setSavingSharepointConfiguration(true);
    this._state.setErrorSharepointConfiguration(null);

    return this._repository.createSharepointConfiguration(data).pipe(
      tap(() => {
        this._state.setSavingSharepointConfiguration(false);
      }),
      map((result) => ({ id: result.id })),
      catchError((error) => {
        this._state.setErrorSharepointConfiguration(error.message || 'Failed to create SharePoint configuration');
        this._state.setSavingSharepointConfiguration(false);
        throw error;
      }),
    );
  }
}
